import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { FaBars, FaTimes, FaChevronDown } from "react-icons/fa";
import { Container } from "../../styles/Layout";
import Logo from "../Logo/Logo";

const NavbarContainer = styled(motion.header)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  padding: 1rem 0;
  transition: all 0.3s cubic-bezier(0.33, 1, 0.68, 1);

  ${(props) =>
    props.$scrolled
      ? `
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
    backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    padding: 0.5rem 0;
  `
      : `
    background: transparent;
  `}
`;

const NavInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NavLinks = styled(motion.nav)`
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: ${(props) => (props.$isOpen ? "0" : "-100%")};
    width: 320px;
    height: 100vh;
    background: linear-gradient(
      135deg,
      rgba(26, 26, 46, 0.95) 0%,
      rgba(22, 33, 62, 0.95) 100%
    );
    backdrop-filter: blur(20px);
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: var(--spacing-xl);
    transition: right 0.4s cubic-bezier(0.33, 1, 0.68, 1);
    border-left: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const NavItem = styled.div`
  position: relative;
  display: inline-block;

  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
  }
`;

const NavLink = styled(motion.a)`
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  position: relative;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s cubic-bezier(0.33, 1, 0.68, 1);

  &::before {
    content: "";
    position: absolute;
    bottom: -2px;
    left: 50%;
    width: 0;
    height: 2px;
    background: linear-gradient(
      135deg,
      var(--neon-blue) 0%,
      var(--neon-magenta) 100%
    );
    transition: all 0.3s ease;
    transform: translateX(-50%);
  }

  &:hover {
    color: var(--neon-blue);
    text-shadow: 0 0 10px currentColor;

    &::before {
      width: 80%;
    }
  }

  @media (max-width: 768px) {
    font-size: 1.2rem;
    padding: 1rem 2rem;

    &:hover {
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.1) 0%,
        rgba(255, 255, 255, 0.05) 100%
      );
      border-radius: 12px;
    }
  }
`;

const DropdownTrigger = styled(motion.div)`
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s cubic-bezier(0.33, 1, 0.68, 1);
  position: relative;

  svg {
    margin-left: 0.5rem;
    font-size: 0.8rem;
    transition: transform 0.3s ease;
  }

  &:hover {
    color: var(--neon-blue);
    text-shadow: 0 0 10px currentColor;

    svg {
      transform: rotate(180deg);
    }
  }

  @media (max-width: 768px) {
    font-size: 1.2rem;
    padding: 1rem 2rem;
    justify-content: center;
  }
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 220px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  margin-top: 0.5rem;

  @media (max-width: 768px) {
    position: static;
    background: transparent;
    backdrop-filter: none;
    border: none;
    box-shadow: none;
    margin-top: 1rem;
  }
`;

const DropdownItem = styled(motion.a)`
  display: block;
  padding: 1rem 1.5rem;
  color: var(--text-secondary);
  font-weight: 500;
  text-decoration: none;
  transition: all 0.3s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(0, 212, 255, 0.1) 0%,
      rgba(255, 0, 128, 0.1) 100%
    );
    color: var(--neon-blue);
    text-shadow: 0 0 10px currentColor;
    padding-left: 2rem;
  }

  @media (max-width: 768px) {
    text-align: center;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    font-size: 1.1rem;

    &:hover {
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.1) 0%,
        rgba(255, 255, 255, 0.05) 100%
      );
      border-radius: 8px;
      padding-left: 1.5rem;
    }
  }
`;

const ContactButton = styled(motion.a)`
  background: linear-gradient(
    135deg,
    var(--neon-blue) 0%,
    var(--neon-magenta) 100%
  );
  color: white;
  padding: 0.8rem 1.8rem;
  border-radius: 12px;
  font-weight: 600;
  text-decoration: none;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);
  transition: all 0.3s cubic-bezier(0.33, 1, 0.68, 1);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover {
    box-shadow: 0 8px 25px rgba(0, 212, 255, 0.5);
    transform: translateY(-2px);

    &::before {
      left: 100%;
    }
  }

  @media (max-width: 768px) {
    padding: 1rem 2rem;
    font-size: 1.1rem;
  }
`;

const MenuToggle = styled(motion.div)`
  display: none;
  color: var(--text-primary);
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 20;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    color: var(--neon-blue);
    text-shadow: 0 0 10px currentColor;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0.05) 100%
    );
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

const CloseButton = styled(motion.div)`
  position: absolute;
  top: 2rem;
  right: 2rem;
  color: var(--text-primary);
  font-size: 1.5rem;
  cursor: pointer;
  display: none;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    color: var(--neon-magenta);
    text-shadow: 0 0 10px currentColor;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0.05) 100%
    );
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => setIsOpen(!isOpen);
  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);
  const closeDropdown = () => setDropdownOpen(false);

  const handleNavClick = (href) => {
    if (href.startsWith("/#")) {
      // If we're not on the home page, navigate to home first
      if (window.location.pathname !== "/") {
        window.location.href = href;
      } else {
        // If we're on home page, smooth scroll to section
        const sectionId = href.substring(2);
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
    setIsOpen(false);
  };

  return (
    <NavbarContainer
      $scrolled={scrolled}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <Container>
        <NavInner>
          <Logo light={true} />

          <MenuToggle
            onClick={toggleMenu}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaBars />
          </MenuToggle>

          <NavLinks $isOpen={isOpen}>
            <CloseButton
              onClick={toggleMenu}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaTimes />
            </CloseButton>

            <NavLink
              href="/"
              onClick={() => {
                setIsOpen(false);
                if (window.location.pathname !== "/") {
                  window.location.href = "/";
                }
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Home
            </NavLink>

            <NavLink
              href="/#about"
              onClick={() => handleNavClick("/#about")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              About
            </NavLink>

            <NavLink
              href="/#tech"
              onClick={() => handleNavClick("/#tech")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Tech Stack
            </NavLink>

            <NavLink
              href="/clients"
              onClick={() => {
                setIsOpen(false);
                if (window.location.pathname !== "/clients") {
                  window.location.href = "/clients";
                }
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Clients
            </NavLink>

            <NavItem>
              <DropdownTrigger
                onClick={toggleDropdown}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Products <FaChevronDown />
              </DropdownTrigger>

              <AnimatePresence>
                {dropdownOpen && (
                  <DropdownMenu
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    <DropdownItem
                      href="/products/edunexus"
                      onClick={closeDropdown}
                      whileHover={{ x: 5 }}
                    >
                      EduNexus
                    </DropdownItem>
                    <DropdownItem
                      href="/products/geocrm"
                      onClick={closeDropdown}
                      whileHover={{ x: 5 }}
                    >
                      GeoCRM
                    </DropdownItem>
                  </DropdownMenu>
                )}
              </AnimatePresence>
            </NavItem>

            <NavLink
              href="/#contact"
              onClick={() => handleNavClick("/#contact")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Contact
            </NavLink>
          </NavLinks>
        </NavInner>
      </Container>
    </NavbarContainer>
  );
};

export default Navbar;
